/**
 * meService.js
 *
 * Single source of truth for the current user's profile, roles, and
 * tenant integration config.  Replaces the old combination of:
 *   - /api/users/check-authorization
 *   - /api/integrations/status/{email}
 *   - /api/integrations/admin-tenants
 *
 * One call → everything the app needs on page load.
 */
import axios from 'axios';
import keycloak from '../utils/keycloakauth';
import { API_BASE_URL } from '../utils/config';

const BASE_URL = API_BASE_URL;

// 5-minute in-memory + sessionStorage cache — keyed per user so switching
// accounts never leaks a previous user's admin status.
const CACHE_DURATION = 5 * 60 * 1000;

let _activeRequest = null; // deduplicate concurrent calls

function _cacheKey() {
  try {
    // Use Keycloak's subject claim (sub) as the cache key discriminator
    const raw = sessionStorage.getItem('me_cache');
    if (raw) {
      const { data } = JSON.parse(raw);
      if (data?.email) return `me_cache_${data.email}`;
    }
  } catch (_) {}
  return 'me_cache';
}

function _getCached() {
  try {
    const raw = sessionStorage.getItem(_cacheKey());
    if (!raw) return null;
    const { data, timestamp } = JSON.parse(raw);
    if (Date.now() - timestamp < CACHE_DURATION) return data;
  } catch (_) {}
  return null;
}

function _setCache(data) {
  try {
    const key = data?.email ? `me_cache_${data.email}` : 'me_cache';
    // Clear any other user's cache to avoid stale data
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith('me_cache') && k !== key) {
        sessionStorage.removeItem(k);
      }
    }
    sessionStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
  } catch (_) {}
}

/**
 * Fetch the full /api/users/me/full response.
 *
 * @param {object} opts
 * @param {boolean} opts.forceRefresh  Skip cache and re-fetch.
 */
export const getMe = async ({ forceRefresh = false } = {}) => {
  if (!forceRefresh) {
    const cached = _getCached();
    if (cached) return cached;
  }

  // Deduplicate: if a request is already in flight, return the same promise
  if (_activeRequest) return _activeRequest;

  const bypass = import.meta.env.VITE_BYPASS_AUTH === 'true';

  _activeRequest = (async () => {
    try {
      if (!bypass && !keycloak.authenticated) {
        throw new Error('User is not authenticated');
      }

      const token = bypass ? 'dev-bypass' : keycloak.token;
      const response = await axios.get(`${BASE_URL}/api/users/me/full`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      _setCache(response.data);
      return response.data;
    } catch (error) {
      console.error('[meService] getMe failed:', error);

      // Fall back to stale cache rather than crashing
      const stale = _getCached();
      if (stale) {
        console.warn('[meService] Returning stale cache after fetch failure');
        return stale;
      }

      // Return a safe default so the app can still render
      return {
        id: null,
        email: keycloak.tokenParsed?.email ?? null,
        first_name: null,
        last_name: null,
        keycloak_id: null,
        roles: [],
        is_admin: false,
        integration: null,
        has_integration: false,
        needs_tenant_selection: false,
        available_tenants: [],
        fetched_at: new Date().toISOString(),
        _error: error.message,
      };
    } finally {
      _activeRequest = null;
    }
  })();

  return _activeRequest;
};

/**
 * Force a fresh fetch and update the cache.
 * Call this after login, logout, integration changes, or role changes.
 */
export const invalidateMe = () => {
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith('me_cache')) sessionStorage.removeItem(k);
    }
  } catch (_) {}
};

/**
 * Quick check — reads from cache only, falls back to a fresh fetch if needed.
 */
export const quickMeCheck = async () => {
  return getMe();
};
